import { useState, useCallback } from 'react';
import type { Picker } from '../types';
import {
    fetchPickersFromDb,
    createPickerInDb,
    deletePickerFromDb,
} from '../services/pickerService';
import { getErrorMessage } from '../utils/errors';

export function usePickers() {
    const [pickers, setPickers] = useState<Picker[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchPickers = useCallback(async (): Promise<Picker[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchPickersFromDb();
            setPickers(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch pickers'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createPicker = useCallback(async (
        picker: Pick<Picker, 'first_name' | 'last_name' | 'store_id'>
    ): Promise<Picker | null> => {
        setError(null);
        try {
            const created = await createPickerInDb(picker);
            setPickers(prev => [...prev, created]);
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create picker'));
            return null;
        }
    }, []);

    const deletePicker = useCallback(async (pickerId: string): Promise<boolean> => {
        setError(null);
        try {
            await deletePickerFromDb(pickerId);
            setPickers(prev => prev.filter(p => String(p.id) !== pickerId));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete picker'));
            return false;
        }
    }, []);

    return { pickers, loading, error, fetchPickers, createPicker, deletePicker };
}